import {
  applyActionCode,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode,
  type User as FirebaseUser,
} from 'firebase/auth';
import { auth } from './firebase';
import { get } from './api';
import type { LoginRequest, User, AuthError, UserProfile } from '../types/auth';

/**
 * Convert Firebase User to our User type
 */
export const mapFirebaseUser = (firebaseUser: FirebaseUser): User => ({
  uid: firebaseUser.uid,
  email: firebaseUser.email,
  displayName: firebaseUser.displayName,
  photoURL: firebaseUser.photoURL,
});

// API response type (flat structure from backend)
type AuthMeResponse = {
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  organizationId: string;
  organizationName?: string;
  sport?: string | null;
  userId: string;
  publicSubmissionToken?: string;
  organizationPaused?: boolean;
  organizationPausedMessage?: string | null;
  organizationAwaitingPayment?: boolean;
  organizationAwaitingPaymentMessage?: string | null;
  messagingEnabled?: boolean;
  permissions?: string[];
};

/**
 * Fetch user profile from backend API
 */
export const fetchUserProfile = async (): Promise<User> => {
  try {
    const response = await get<AuthMeResponse>('/api/auth/me');

    // Get current Firebase user
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      throw new Error('No authenticated Firebase user');
    }

    // Map API response to UserProfile type
    const profile: UserProfile = {
      id: response.userId,
      email: response.email,
      name: [response.firstName, response.lastName].filter(Boolean).join(' ') || null,
      organizationId: response.organizationId,
      organizationName: response.organizationName || '',
      sport: response.sport ?? null,
      role: response.role,
      publicSubmissionToken: response.publicSubmissionToken,
      // Default to not-paused so an older or partial response can never lock a user out
      organizationPaused: response.organizationPaused ?? false,
      organizationPausedMessage: response.organizationPausedMessage ?? null,
      // Only an explicit true locks the app, so an older API cannot send a paying org to checkout.
      organizationAwaitingPayment: response.organizationAwaitingPayment === true,
      organizationAwaitingPaymentMessage:
        response.organizationAwaitingPayment === true
          ? (response.organizationAwaitingPaymentMessage ?? null)
          : null,
      // Default true so an older or partial response cannot hide a section the org actually pays
      // for. Hiding on a missing field would look identical to being switched off.
      messagingEnabled: response.messagingEnabled ?? true,
      // Deliberately left undefined when absent rather than defaulted to a list. Undefined means
      // unrestricted, so an older API — or a response that lost the field — cannot strip pages from
      // every coach at once. Defaulting to [] here would do exactly that.
      permissions: response.permissions,
      createdAt: '',
      updatedAt: '',
    };

    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      profile,
    };
  } catch (error: unknown) {
    const apiError = error as { message: string; statusCode?: number };
    throw {
      code: apiError.statusCode === 401 ? 'auth/unauthorized' : 'auth/profile-fetch-failed',
      message: apiError.message || 'Failed to fetch user profile',
    } as AuthError;
  }
};

/**
 * Sign in with email and password
 */
export const login = async ({ email, password }: LoginRequest): Promise<User> => {
  try {
    // Step 1: Authenticate with Firebase
    await signInWithEmailAndPassword(auth, email, password);

    // Step 2: Fetch user profile from backend to verify the user exists
    // (Token is now retrieved on-demand via getCurrentUserToken())
    const user = await fetchUserProfile();

    return user;
  } catch (error: unknown) {
    const authError = error as { code?: string; message?: string };
    throw {
      code: authError.code || 'auth/unknown-error',
      message: authError.message || 'An error occurred during login',
    } as AuthError;
  }
};

/**
 * Sign out the current user
 */
export const logout = async (): Promise<void> => {
  try {
    await signOut(auth);
  } catch (error: unknown) {
    const authError = error as { code: string; message: string };
    throw {
      code: authError.code || 'auth/unknown-error',
      message: authError.message || 'An error occurred during logout',
    } as AuthError;
  }
};

/**
 * Subscribe to auth state changes
 */
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (firebaseUser) {
      try {
        // Fetch user profile from backend
        // (Token is now retrieved on-demand via getCurrentUserToken())
        const user = await fetchUserProfile();
        callback(user);
      } catch (error) {
        console.error('Failed to fetch user profile on auth state change:', error);
        // Fallback to Firebase user data only. A 401 from /api/auth/me with a freshly issued
        // Firebase token means there is no RosterPilot user for this account (an unfinished
        // self-serve signup), which the routes treat differently from a transient failure.
        const user: User = {
          ...mapFirebaseUser(firebaseUser),
          ...((error as AuthError | undefined)?.code === 'auth/unauthorized'
            ? { profileMissing: true }
            : {}),
        };
        callback(user);
      }
    } else {
      callback(null);
    }
  });
};

/**
 * Get the current user's ID token
 */
export const getCurrentUserToken = async (): Promise<string | null> => {
  const currentUser = auth.currentUser;
  if (currentUser) {
    return await currentUser.getIdToken();
  }
  return null;
};

/**
 * Request a password reset email
 */
export const requestPasswordReset = async (email: string): Promise<void> => {
  await sendPasswordResetEmail(auth, email);
};

/**
 * Complete password reset with the oob code from the email
 */
export const resetPassword = async (oobCode: string, newPassword: string): Promise<void> => {
  await confirmPasswordReset(auth, oobCode, newPassword);
};

/**
 * Verify the password reset code and return the associated email
 */
export const verifyResetCode = async (oobCode: string): Promise<string> => {
  return verifyPasswordResetCode(auth, oobCode);
};

/* ────────────────────────────────────────────────────────────────────────────
   Self-serve signup. The Firebase half of creating a program: the account, the
   verification email, and the check that the address has been verified. None of
   these fetch a RosterPilot profile, because there is none until the API has
   created the program.
   ──────────────────────────────────────────────────────────────────────────── */

/** Creates the Firebase account (and signs it in). Throws Firebase's own error, with its `code`. */
export const createSignupAccount = async (email: string, password: string): Promise<void> => {
  await createUserWithEmailAndPassword(auth, email, password);
};

/**
 * Signs an existing Firebase account in without fetching a profile. Used to resume a signup whose
 * Firebase account was created on an earlier visit.
 */
export const signInForSignup = async (email: string, password: string): Promise<void> => {
  await signInWithEmailAndPassword(auth, email, password);
};

/**
 * Sends (or re-sends) the verification email to the signed-in account.
 *
 * The link itself goes to the project's custom action handler (`/auth/complete-reset`, the same URL
 * every Firebase email template uses); the continue URL is what that page offers afterwards, so it
 * points back at signup, where the coach's form is waiting.
 */
export const sendSignupVerificationEmail = async (): Promise<void> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('No signed-in account to verify');
  }
  await sendEmailVerification(currentUser, { url: `${window.location.origin}/signup` });
};

/**
 * Applies a `mode=verifyEmail` action code from a verification link. Throws Firebase's own error
 * (with its `code`) for an expired, used or malformed code.
 *
 * Deliberately does not sign anyone out: the coach who opens the link is usually mid-signup in
 * another tab of this browser, and that session is what "I've verified" continues with. When this
 * tab shares it, the account is reloaded so it reads as verified straight away.
 */
export const applyEmailVerificationCode = async (oobCode: string): Promise<void> => {
  await applyActionCode(auth, oobCode);
  try {
    await auth.currentUser?.reload();
  } catch {
    // Best effort: "I've verified" reloads the account again anyway.
  }
};

/** Whether a Firebase account is signed in right now, with or without a RosterPilot profile. */
export const hasFirebaseSession = (): boolean => auth.currentUser !== null;

/**
 * Reports Firebase's own signed-in state, immediately and on every change, without fetching a
 * profile. Signup watches this rather than AuthContext's user, which lags behind a profile request
 * and so cannot tell "not loaded yet" from "signed out in another tab".
 */
export const subscribeToFirebaseSession = (callback: (signedIn: boolean) => void): (() => void) =>
  onAuthStateChanged(auth, (firebaseUser) => callback(firebaseUser !== null));

/** Whether the signed-in account's address is verified, as of the last reload. */
export const isCurrentUserEmailVerified = (): boolean => auth.currentUser?.emailVerified === true;

/**
 * Re-reads the signed-in account from Firebase and reports whether its address is now verified.
 *
 * When it is, the ID token is force-refreshed before returning: the API reads `email_verified` from
 * the token, and one minted before the coach clicked the link still says false. Every later request
 * picks up the refreshed token, since `getIdToken()` returns the cached one.
 */
export const refreshEmailVerified = async (): Promise<boolean> => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('No signed-in account to verify');
  }

  await currentUser.reload();
  const refreshed = auth.currentUser ?? currentUser;

  if (!refreshed.emailVerified) {
    return false;
  }

  await refreshed.getIdToken(true);
  return true;
};
